import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Switch,
  Typography,
  Alert,
} from '@mui/material';
import { FaceRetouchingNatural as FaceIcon, DeleteOutline as DeleteIcon } from '@mui/icons-material';
import FaceCapture from '../auth/FaceCapture';
import {
  enrollFaceLogin,
  getFaceLoginStatus,
  removeFaceEnrollment,
  updateFaceLoginEnabled,
} from '../../services/api';

import {
  DASHBOARD_ACCENT,
  getProfilePanelHeaderSx,
  getProfilePrimaryButtonSx,
  getProfileSectionIconSx,
  getProfileSurfaceTokens,
} from '../../utils/profilePageLayout';

export default function ProfileFaceLoginSection({ isDark, onNotify, cardSx = {} }) {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [enrolling, setEnrolling] = useState(false);
  const [showCapture, setShowCapture] = useState(false);
  const [error, setError] = useState(null);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getFaceLoginStatus();
      setStatus(data);
    } catch (err) {
      const message = err?.errors?.non_field_errors?.[0] || err?.message || 'Failed to load face login status.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleEnrollCapture = async (blob) => {
    setEnrolling(true);
    setError(null);
    try {
      const data = await enrollFaceLogin(blob);
      setStatus(data);
      setShowCapture(false);
      onNotify?.('Face enrolled successfully. Face login is now enabled.');
    } catch (err) {
      const message = err?.errors?.non_field_errors?.[0] || err?.message || 'Face enrollment failed.';
      setError(message);
    } finally {
      setEnrolling(false);
    }
  };

  const handleToggle = async (event) => {
    const enabled = event.target.checked;
    setError(null);
    try {
      const data = await updateFaceLoginEnabled(enabled);
      setStatus(data);
      onNotify?.(enabled ? 'Face login enabled.' : 'Face login disabled.');
    } catch (err) {
      const message = err?.errors?.non_field_errors?.[0] || err?.message || 'Unable to update face login.';
      setError(message);
      await loadStatus();
    }
  };

  const handleRemove = async () => {
    setError(null);
    try {
      await removeFaceEnrollment();
      await loadStatus();
      setShowCapture(false);
      onNotify?.('Face enrollment removed.');
    } catch (err) {
      const message = err?.errors?.non_field_errors?.[0] || err?.message || 'Unable to remove face enrollment.';
      setError(message);
    }
  };

  const tokens = getProfileSurfaceTokens(isDark);

  if (loading) {
    return (
      <Card elevation={0} sx={{ mt: 2, ...cardSx }}>
        <CardContent sx={{ p: { xs: 2, sm: 2.5 }, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={28} sx={{ color: DASHBOARD_ACCENT }} />
        </CardContent>
      </Card>
    );
  }

  const serviceAvailable = status?.service_available;
  const enrolled = status?.enrolled;
  const enabled = status?.enabled;

  return (
    <Card elevation={0} sx={{ mt: 2, ...cardSx }}>
      <Box sx={getProfilePanelHeaderSx(isDark)}>
        <Box sx={getProfileSectionIconSx(isDark)}>
          <FaceIcon sx={{ fontSize: 18 }} />
        </Box>
        <Typography sx={{ fontWeight: 700, color: tokens.text, fontSize: '1rem', flex: 1 }}>Face Login</Typography>
        {enrolled ? (
          <Chip
            size="small"
            label={enabled ? 'Enabled' : 'Disabled'}
            sx={{
              backgroundColor: enabled ? 'rgba(76, 225, 182, 0.15)' : 'rgba(158, 158, 158, 0.15)',
              color: enabled ? DASHBOARD_ACCENT : tokens.subtleText,
              fontWeight: 700,
            }}
          />
        ) : null}
      </Box>
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Typography variant="body2" sx={{ color: tokens.subtleText, mb: 2 }}>
          Register your face to sign in after password without waiting for email OTP. Biometric data is
          stored encrypted on the server; raw photos are not kept.
        </Typography>
        <Divider sx={{ mb: 2, borderColor: tokens.surfaceBorder }} />

        {!serviceAvailable && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Face login is not enabled on this server. Ask your administrator to configure Google Cloud Vision.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {serviceAvailable && (
          <>
            {enrolled && (
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(enabled)}
                    onChange={handleToggle}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: DASHBOARD_ACCENT },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: DASHBOARD_ACCENT,
                      },
                    }}
                  />
                }
                label="Use face verification at login"
                sx={{ color: tokens.text, mb: 2, display: 'block' }}
              />
            )}

            {showCapture ? (
              <FaceCapture
                variant={isDark ? 'dark' : 'light'}
                onCapture={handleEnrollCapture}
                isSubmitting={enrolling}
                captureLabel={enrolled ? 'Update enrolled face' : 'Enroll face'}
                helperText="Use a clear front-facing photo in good lighting. Remove glasses or hats if possible."
              />
            ) : (
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', gap: 1.5 }}>
                <Button
                  variant="contained"
                  fullWidth
                  disabled={!serviceAvailable || enrolling}
                  onClick={() => setShowCapture(true)}
                  sx={{
                    textTransform: 'none',
                    ...getProfilePrimaryButtonSx(),
                    maxWidth: { sm: 'none' },
                  }}
                >
                  {enrolled ? 'Update face' : 'Register face'}
                </Button>
                {enrolled && (
                  <Button
                    variant="outlined"
                    color="error"
                    fullWidth
                    startIcon={<DeleteIcon />}
                    onClick={handleRemove}
                    sx={{ textTransform: 'none', maxWidth: { sm: 'none' } }}
                  >
                    Remove enrollment
                  </Button>
                )}
              </Box>
            )}

            {showCapture && (
              <Button
                variant="text"
                onClick={() => setShowCapture(false)}
                sx={{ mt: 1.5, textTransform: 'none', color: tokens.subtleText }}
              >
                Cancel
              </Button>
            )}

            {status?.enrolled_at && (
              <Typography variant="caption" sx={{ display: 'block', mt: 2, color: tokens.subtleText }}>
                Enrolled: {status.enrolled_at}
              </Typography>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
